import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getBlogPost, mediaUrl } from "@/lib/api";
import Seo from "@/components/Seo";
import { articleSchema, breadcrumbSchema } from "@/lib/schemas";

const EASE = [0.76, 0, 0.24, 1];

export default function InsightDetailPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    getBlogPost(slug).then(setPost).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="bg-[var(--bg)] min-h-screen pt-40 pb-24 flex items-center justify-center">
        <Seo title="Post not found" noIndex path={`/insights/${slug}`} />
        <div className="text-center">
          <p className="font-serif-accent italic text-[var(--accent)] text-2xl mb-6">
            That essay isn&apos;t here.
          </p>
          <Link to="/insights" className="btn-pill">
            <span>Back to all essays</span>
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="bg-[var(--bg)] min-h-screen pt-40 pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-10 animate-pulse space-y-4">
          <div className="h-4 w-24 bg-[var(--bg-2)]" />
          <div className="h-12 w-2/3 bg-[var(--bg-2)]" />
          <div className="h-4 w-1/2 bg-[var(--bg-2)]" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[var(--bg)] min-h-screen pt-40 pb-24">
      <Seo
        title={post.title}
        description={post.excerpt}
        path={`/insights/${post.slug}`}
        type="article"
        jsonLd={[
          articleSchema(post),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Insights", path: "/insights" },
            { name: post.title, path: `/insights/${post.slug}` },
          ]),
        ]}
      />

      <article className="max-w-3xl mx-auto px-6 md:px-10">
        <Link
          to="/insights"
          className="link-slide inline-flex items-center gap-2 text-sm uppercase tracking-[0.22em] text-[var(--ink-70)] mb-10"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>All essays</span>
        </Link>

        <div className="flex flex-wrap items-center gap-2 mb-6">
          {(post.tags || []).map((t) => (
            <span
              key={t}
              className="text-[10px] uppercase tracking-[0.22em] text-[var(--accent)] border border-[var(--accent)]/40 px-2 py-1"
            >
              {t}
            </span>
          ))}
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="font-display text-4xl md:text-6xl tracking-[-0.03em] leading-[0.95] text-[var(--ink)] mb-6"
        >
          {post.title}
        </motion.h1>

        <p className="text-lg text-[var(--ink-80)] leading-relaxed mb-8">
          {post.excerpt}
        </p>

        <p className="text-xs uppercase tracking-[0.22em] text-[var(--ink-50)] mb-10 pb-6 border-b border-[var(--line-strong)]">
          {post.author || "Orphion"} ·{" "}
          {new Date(post.published_at).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
        </p>

        {post.cover_image && (
          <div className="clip-frame aspect-[16/9] mb-12">
            <img src={mediaUrl(post.cover_image)} alt={post.title} />
          </div>
        )}

        <div className="prose prose-lg max-w-none prose-headings:font-display prose-headings:tracking-tight prose-headings:text-[var(--ink)] prose-p:text-[var(--ink)] prose-p:leading-relaxed prose-a:text-[var(--accent)] prose-strong:text-[var(--ink)] prose-li:text-[var(--ink)]">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body}</ReactMarkdown>
        </div>

        <div className="mt-16 pt-10 border-t border-[var(--line-strong)] flex flex-col sm:flex-row justify-between items-start gap-6">
          <p className="font-serif-accent italic text-xl text-[var(--ink)] max-w-md">
            Have a similar people-decision on your table?
          </p>
          <Link to="/contact" className="btn-pill">
            <span>Start a conversation</span>
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </article>
    </div>
  );
}
